import { useState, type ReactNode } from 'react';
import { BookOpen, HelpCircle } from 'lucide-react';
import clsx from 'clsx';

/** Las dos ayudas del CRM, para que nadie tenga que preguntar.
 *
 *  - `AyudaGuia`: en la cabecera de cada vista. Abre la guía (public/guia.html)
 *    justo en la sección de esa vista, en pestaña nueva.
 *  - `Pregunta`: la «preguntita» de un bloque. Un «?» que despliega la
 *    explicación ahí mismo, sin salir de la página.
 */

export type SeccionGuia =
  | 'viviendas'
  | 'clientes'
  | 'incidencias'
  | 'operaciones'
  | 'comisiones'
  | 'contabilidad'
  | 'cartera'
  | 'agenda'
  | 'costes'
  | 'servicios'
  | 'ajustes'
  | 'cuentas'
  | 'tu-web';

export function AyudaGuia({ seccion }: { seccion: SeccionGuia }) {
  return (
    <a
      href={`/guia.html#${seccion}`}
      target="_blank"
      rel="noopener"
      title="Abrir la guía de esta sección"
      className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-deep/15 px-3 py-1 text-xs font-bold text-ink/60 transition hover:border-brand hover:text-brand"
    >
      <BookOpen size={14} strokeWidth={2} />
      Guía
    </a>
  );
}

export function Pregunta({ titulo, children }: { titulo: string; children: ReactNode }) {
  const [abierta, setAbierta] = useState(false);
  return (
    <div className="mt-1">
      <button
        type="button"
        onClick={() => setAbierta((v) => !v)}
        aria-expanded={abierta}
        className={clsx(
          'inline-flex items-center gap-1.5 text-xs font-semibold transition',
          abierta ? 'text-brand' : 'text-ink/50 hover:text-brand',
        )}
      >
        <HelpCircle size={14} strokeWidth={2} />
        {titulo}
      </button>
      {abierta && (
        <div className="mt-2 rounded-xl bg-soft px-4 py-3 text-sm leading-relaxed text-ink/75">
          {children}
        </div>
      )}
    </div>
  );
}
